import { useEffect, useRef, useState } from 'react';
import { formatBodyFat } from '../../design-system/lib/metric-format';
import { TargetSlider, type Zone } from '../../design-system/target/target-slider';
import { CoachCard } from '../../design-system/concept/coach-card';
import { Eyebrow } from '../../design-system/concept/eyebrow';
import { TwinView } from '../coach/twin-view';
import type { GoalProjection } from '../../lib/dashboard/goalProjection';
import type { ObjectModel } from '../../lib/types';

const Row: React.FC<{ label: string; value: string; muted?: boolean }> = ({
  label,
  value,
  muted,
}) => (
  <>
    <span className="text-xs text-foreground-soft">{label}</span>
    <span
      // A value that is not there reads as an absence, not a figure.
      className={`tabular text-xs ${muted ? 'text-muted-foreground' : 'font-semibold'}`}
    >
      {value}
    </span>
  </>
);

/** How long the slider rests before the tried body is built. */
const TRY_AFTER_MS = 450;

/** Shown over the projected twin while /generate is answering. */
const Regenerating: React.FC = () => (
  <div
    data-testid="twin-regenerating"
    className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-card/75"
  >
    <span className="h-5 w-5 animate-spin rounded-full border-2 border-divider border-t-[hsl(var(--projected))]" />
    <span className="font-mono text-[8.5px] uppercase tracking-[0.1em] text-[hsl(var(--projected))]">
      Rebuilding
    </span>
  </div>
);

/**
 * "Mitt mål" — the body-fat percentage translated into things a person can
 * picture, and the twin that goes with it.
 *
 * The concept is explicit that this is where anything unmeasured lives: one
 * place for the simulated body, so nothing projected leaks onto a screen of
 * scans.
 *
 * Now and Projected sit side by side. Seeing the target body alone answers
 * "what would I look like"; seeing it beside today's answers "how far is
 * that", which is the question a target is set to settle.
 */
export const GoalSection: React.FC<{
  currentPercent: number;
  targetPercent?: number;
  /**
   * Lean mass the projection holds constant, in kilograms, from the latest
   * weighed scan. Absent when no scan carries a weight: kilograms cannot be
   * split without one, and showing "≈ 0.0 kg" would be a wrong number rather
   * than a missing one. The body and its waist do not need it — they are
   * built from the target alone — so they are shown either way.
   */
  leanHeldKg?: number;
  zones: Zone[];
  age?: number;
  goal: GoalProjection | null;
  goalModel: ObjectModel | null;
  nowModel: ObjectModel | null;
  /** True while /generate is answering for the body shown. */
  regenerating?: boolean;
  /**
   * The value the slider came to rest on, before anything is saved, or null
   * when the coach leaves. The parent builds that body and passes it back as
   * `goal`, so the projected twin follows the slider as the figures do.
   */
  onTry?: (percent: number | null) => void;
  /** Resolves false when the save failed; see TargetSlider. */
  onCommit: (percent: number) => void | Promise<boolean>;
}> = ({
  currentPercent,
  targetPercent,
  leanHeldKg,
  zones,
  age,
  goal,
  goalModel,
  nowModel,
  regenerating,
  onTry,
  onCommit,
}) => {
  const [preview, setPreview] = useState<number | null>(null);
  const target = preview ?? targetPercent ?? Math.max(5, Math.round(currentPercent) - 5);

  // Build the tried body once the slider rests, not on every tick: each value
  // is a /generate round trip, and a drag passes through a dozen.
  const onTryRef = useRef(onTry);
  onTryRef.current = onTry;
  useEffect(() => {
    if (preview === null) return;
    const timer = setTimeout(() => onTryRef.current?.(preview), TRY_AFTER_MS);
    return () => clearTimeout(timer);
  }, [preview]);
  // The tried value stands until the save lands, so the label and the body
  // do not step back to the old target in between.
  useEffect(() => setPreview(null), [targetPercent]);
  // Leaving the page drops the tried value, so coming back shows the target.
  useEffect(() => () => onTryRef.current?.(null), []);

  // Until the body for this value arrives, the twin says it is not that body
  // yet rather than stand under a label it does not match.
  const twinBusy = regenerating || (goal !== null && goal.targetBodyFat !== target);

  // Weight and fat at a target are arithmetic on held lean mass, so they can
  // follow the drag exactly. The waist cannot: it is measured off a generated
  // mesh, which only exists once the slider has rested and /generate has
  // answered. Showing a stale waist beside live figures would be the one
  // number on the card that is quietly wrong.
  const weightKg = leanHeldKg === undefined ? undefined : leanHeldKg / (1 - target / 100);
  const fatKg = weightKg === undefined || leanHeldKg === undefined ? undefined : weightKg - leanHeldKg;
  const NEEDS_WEIGHT = 'needs a weighed scan';
  const waistIsCurrent = goal !== null && goal.targetBodyFat === target && !regenerating;

  const twin = (
    model: ObjectModel | null,
    opts: { projected?: boolean; busy?: boolean } = {},
  ) => (
    // Shorter on a phone: at 280px the two stacked pairs push the figures
    // below the fold, and the figures are the point of the screen.
    <div className="relative flex h-[210px] gap-2 sm:h-[280px]">
      {opts.busy && <Regenerating />}
      <TwinView
        model={model}
        projected={opts.projected}
        generated={opts.projected}
        orbit
        className="flex-[1.6]"
      />
      <TwinView
        model={model}
        projected={opts.projected}
        generated={opts.projected}
        side
        orbit
        className="hidden flex-1 sm:block"
      />
    </div>
  );

  // Order differs by viewport. On a phone the figures sit directly under the
  // slider, because that is where the eye is while dragging — below two
  // stacked 210px bodies they were a scroll away from the control that moves
  // them. On desktop the bodies sit side by side and everything fits, so they
  // keep the concept's order: slider, bodies, figures.
  return (
    <div className="flex flex-col gap-3.5">
      <TargetSlider
        currentPercent={currentPercent}
        targetPercent={targetPercent}
        zones={zones}
        age={age}
        onPreview={setPreview}
        onCommit={onCommit}
        saving={regenerating}
        holdsLean={leanHeldKg !== undefined}
      />

      {/* Now against Projected. Stacked on a phone, where two pairs side by
          side leave each body about 80px wide. */}
      <div data-testid="goal-twins" className="order-3 grid gap-3.5 lg:order-2 lg:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <Eyebrow tone="primary">Now · measured</Eyebrow>
            <span data-testid="goal-now" className="tabular text-xs font-semibold">
              {formatBodyFat(currentPercent)}
            </span>
          </div>
          {twin(nowModel)}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <Eyebrow tone="projected">Goal · {target} % body fat</Eyebrow>
            <span className="projected-label shrink-0 rounded px-1.5 py-1 font-mono text-[8.5px] font-semibold uppercase tracking-wider">
              Simulated
            </span>
          </div>
          {twin(goalModel, { projected: true, busy: twinBusy })}
        </div>
      </div>

      <CoachCard
        testId="goal-translation"
        className="order-2 lg:order-3"
        projected
        title={`At ${target} % body fat`}
        provenance={
          leanHeldKg === undefined
            ? 'The body and its waist are simulated from the target body fat; the waist is measured off the generated mesh, by the same definition as a scan. Weight and fat mass need a weighed scan.'
            : 'Weight and fat mass follow from holding lean mass. Waist is measured off the generated mesh, by the same definition as a scan.'
        }
      >
        <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2 sm:grid-cols-[1fr_auto_1fr_auto]">
          <Row
            label="Weight"
            value={weightKg === undefined ? NEEDS_WEIGHT : `≈ ${weightKg.toFixed(1)} kg`}
            muted={weightKg === undefined}
          />
          <Row
            label="Fat mass"
            value={fatKg === undefined ? NEEDS_WEIGHT : `≈ ${fatKg.toFixed(1)} kg`}
            muted={fatKg === undefined}
          />
          <Row
            label="Waist"
            value={
              regenerating
                ? 'measuring…'
                : waistIsCurrent && goal.waistCm !== null
                  ? `≈ ${Math.round(goal.waistCm)} cm`
                  : // The body is built from the target alone, so this
                    // holds with or without a weighed scan.
                    'set the target to measure'
            }
            muted={!waistIsCurrent}
          />
          <Row
            label="Lean"
            value={leanHeldKg === undefined ? NEEDS_WEIGHT : `${leanHeldKg.toFixed(1)} kg held`}
            muted={leanHeldKg === undefined}
          />
        </div>
      </CoachCard>
    </div>
  );
};
